import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../../layout/Icon.jsx';
import { Avatar } from '../../../layout/Avatar.jsx';
import { api, apiError, fieldErrors } from '../../../shared/api.js';
import { toast } from '../../../shared/useToast.js';
import { useAuth } from '../../../shared/useAuth.js';

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/api/') || url.startsWith('/uploads/')) return url;
  return `/api/children/photos/${url.replace(/^\//, '')}`;
}

// Modal تعديل الطفل من شاشة الأطفال داخل القاعة.
// المنطق: بما أن الطفل في جلسة نشطة، الـ backend سيرفض التعديل (Wave 2B).
// نعرض الحقول للقراءة مع شريط تنبيه واضح يوضّح سبب القفل.
// إذا أُغلقت الجلسة لاحقاً، نفس الـ modal سيدعم التعديل تلقائياً.
export function ChildEditModal({ open, session, onClose }) {
  const qc = useQueryClient();
  const hasPermission = useAuth((s) => s.hasPermission);
  const child = session?.childId || session?.child || {};
  const guardian = session?.guardianId || session?.guardian || {};
  const childId = child._id || child.id || null;
  const sessionActive = session?.status === 'active' || session?.status === 'overstay' || session?.status === 'expired';
  const canBypass = hasPermission('reception.edit_locked_child');
  const locked = sessionActive && !canBypass;

  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('male');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [medicalNotes, setMedicalNotes] = useState('');
  const [allergyNotes, setAllergyNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && child) {
      setName(child.name || '');
      setAge(child.age != null ? String(child.age) : '');
      setGender(child.gender || 'male');
      setHeight(child.height != null ? String(child.height) : '');
      setWeight(child.weight != null ? String(child.weight) : '');
      setMedicalNotes(child.medicalNotes || '');
      setAllergyNotes(child.allergyNotes || '');
      setErrors({});
    }
  }, [open, child]);

  if (!open || !session) return null;

  async function handleSave() {
    if (locked || !childId) return;
    setErrors({});
    setSubmitting(true);
    try {
      await api.patch(`/children/${childId}`, {
        name: name.trim(),
        age: Number(age) || 0,
        gender,
        height: height ? Number(height) : null,
        weight: weight ? Number(weight) : null,
        medicalNotes: medicalNotes.trim(),
        allergyNotes: allergyNotes.trim()
      });
      toast.success('تم حفظ التعديلات');
      qc.invalidateQueries({ queryKey: ['active-sessions'] });
      qc.invalidateQueries({ queryKey: ['children'] });
      onClose?.();
    } catch (err) {
      const map = fieldErrors(err);
      if (Object.keys(map).length) setErrors(map);
      else toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const photoUrl = resolvePhoto(child.photoUrl);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(15,16,18,0.5)' }}
    >
      <div
        className="bg-white rounded-[14px] shadow-card w-[640px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <div className="flex items-center gap-3 flex-1">
            <Avatar src={photoUrl} name={child.name || '?'} id={childId} size="md" />
            <div>
              <h3 className="text-[15px] font-bold m-0">معلومات الطفل</h3>
              <div className="text-xs text-ink-3 mt-0.5">
                ولي الأمر: {guardian.name || '—'}{guardian.phone ? ` • ${guardian.phone}` : ''}
              </div>
            </div>
          </div>
          <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {locked ? (
          <div
            className="mx-4 mt-3 p-3 rounded-[10px] flex items-start gap-2 text-[12.5px]"
            style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}
          >
            <Icon name="clock" size={16} />
            <div>
              <b>الجلسة نشطة حالياً</b> — لا يمكن تعديل معلومات الطفل أثناء وجود جلسة مفتوحة.
              بعد إنهاء الجلسة من شاشة "تسوية وخروج"، يمكنك العودة لتعديل البيانات.
            </div>
          </div>
        ) : sessionActive && canBypass ? (
          <div
            className="mx-4 mt-3 p-3 rounded-[10px] flex items-start gap-2 text-[12.5px]"
            style={{ background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' }}
          >
            <span className="text-[14px]">⚠️</span>
            <div>
              <b>تنبيه:</b> الجلسة نشطة. تستطيع التعديل بصلاحيتك الإدارية — تأكد من صحة التعديل قبل الحفظ.
            </div>
          </div>
        ) : null}

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">اسم الطفل *</label>
              <input
                className={clsx('input', errors.name && 'border-red border-2')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={locked}
              />
              {errors.name ? <div className="text-red text-[11px] mt-1">{errors.name}</div> : null}
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">العمر *</label>
              <input
                className={clsx('input', errors.age && 'border-red border-2')}
                type="number"
                min={0}
                max={18}
                value={age}
                onChange={(e) => setAge(e.target.value)}
                disabled={locked}
              />
              {errors.age ? <div className="text-red text-[11px] mt-1">{errors.age}</div> : null}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الجنس</label>
            <div className="flex gap-1.5">
              {[
                { key: 'male', label: 'ذكر' },
                { key: 'female', label: 'أنثى' }
              ].map((g) => (
                <button
                  key={g.key}
                  type="button"
                  className={`btn ${gender === g.key ? 'btn-primary' : ''}`}
                  style={{ padding: '6px 14px', fontSize: 12 }}
                  onClick={() => !locked && setGender(g.key)}
                  disabled={locked}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الطول (سم)</label>
              <input
                className={clsx('input', errors.height && 'border-red border-2')}
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                disabled={locked}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الوزن (كغ)</label>
              <input
                className={clsx('input', errors.weight && 'border-red border-2')}
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                disabled={locked}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات صحية</label>
              <textarea
                className={clsx('input', errors.medicalNotes && 'border-red border-2')}
                rows={2}
                value={medicalNotes}
                onChange={(e) => setMedicalNotes(e.target.value)}
                disabled={locked}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">حساسية</label>
              <textarea
                className={clsx('input', errors.allergyNotes && 'border-red border-2')}
                rows={2}
                value={allergyNotes}
                onChange={(e) => setAllergyNotes(e.target.value)}
                disabled={locked}
              />
            </div>
          </div>
        </div>

        <div className="card-h border-b-0 border-t flex gap-2 justify-end">
          <button type="button" className="btn" onClick={onClose}>
            {locked ? 'إغلاق' : 'إلغاء'}
          </button>
          {!locked ? (
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={submitting}>
              <Icon name="check" size={14} />
              {submitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
